import { Card, InputLabel } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { DividingLine } from 'common/assets/images';
import { ReactComponent as DialHigh } from 'common/assets/images/dialHigh.svg';
import { ReactComponent as DialLow } from 'common/assets/images/dialLow.svg';
import { ReactComponent as DialMedium } from 'common/assets/images/dialMedium.svg';
import { ReactComponent as FlagIcon } from 'common/assets/images/flag.svg';
import { ReactComponent as FolderIcon } from 'common/assets/images/folderOpen.svg';
import { Loader } from 'common/components/Loader/Loader';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { formatNumber, toSentenceCase } from 'common/utils/utilFunctions';

import { getStatusBarCount } from './api';
import { IStatusBarProps } from './api/type-def';

import styles from './StatusBar.module.scss';

const StatusBar = (props: IStatusBarProps) => {
	const { searchText } = props;
	const [, setError] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const [statusBarData, setStatusBarData] = useState<any>({
		updateCount: 0,
		openTask: 0,
		highImpactCount: 0,
		mediumImpactCount: 0,
		lowImpactCount: 0,
	});
	const dispatch = useDispatch();
	const horizonFilterData = useSelector(
		(state: RootState) => state.horizonScan.horizonFilters,
	);
	const { OK, BAD_REQUEST } = HTTP_STATUS_CODES;

	const {
		chipPriorityHigh,
		chipPriorityMedium,
		chipPriorityLow,
		statusBarSuccess,
	} = styles;
	const getAllMyTasksData = async () => {
		setError(false);
		setIsLoading(true);
		const paramsObj = { searchText, filter: horizonFilterData };
		const response = await getStatusBarCount(paramsObj);

		if (response?.statusCode === OK) {
			const responseData = response?.responseBody;
			setStatusBarData(responseData);
			setIsLoading(false);
		} else if (response?.statusCode === BAD_REQUEST) {
			const { errorBody } = response;
			const errorMsg = errorBody?.errorMessage as string;
			dispatch(
				setToastMessage({
					message: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	useEffect(() => {
		getAllMyTasksData();
	}, [searchText, horizonFilterData]);

	return (
		<Card className={styles['status-bar-stats-card']}>
			{isLoading ? (
				<Loader show={isLoading} className={styles['count-loader']} />
			) : (
				<>
					<span className={styles['status-bar-stats-card-content-span']}>
						<InputLabel className={styles['status-bar-stats-card-header']}>
							Updates
						</InputLabel>
						<div className={styles['row-container']}>
							<FlagIcon className={styles['status-bar-icon']} />
							<InputLabel className={styles['status-bar-stats-card-number']}>
								{formatNumber(statusBarData.updateCount)}
							</InputLabel>
						</div>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<img
							className={styles['status-bar-stats-card-divider']}
							src={DividingLine}
							alt=""
						/>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<InputLabel className={styles['status-bar-stats-card-header']}>
							{toSentenceCase('Open Tasks')}
						</InputLabel>
						<div className={styles['row-container']}>
							<FolderIcon
								fill={statusBarSuccess}
								className={styles['status-bar-icon']}
							/>
							<InputLabel className={styles['status-bar-stats-card-number']}>
								{formatNumber(statusBarData.openTask)}
							</InputLabel>
						</div>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<img
							className={styles['status-bar-stats-card-divider']}
							src={DividingLine}
							alt=""
						/>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<InputLabel className={styles['status-bar-stats-card-header']}>
							{toSentenceCase('High Impact')}
						</InputLabel>
						<div className={styles['row-container']}>
							<DialHigh
								className={styles['status-bar-high-icon']}
								fill={chipPriorityHigh}
							/>
							<InputLabel className={styles['status-bar-stats-card-number']}>
								{formatNumber(statusBarData.highImpactCount)}
							</InputLabel>
						</div>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<img
							className={styles['status-bar-stats-card-divider']}
							src={DividingLine}
							alt=""
						/>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<InputLabel className={styles['status-bar-stats-card-header']}>
							{toSentenceCase('Medium Impact')}
						</InputLabel>
						<div className={styles['row-container']}>
							<DialMedium
								className={styles['status-bar-medium-icon']}
								fill={chipPriorityMedium}
							/>
							<InputLabel className={styles['status-bar-stats-card-number']}>
								{formatNumber(statusBarData.mediumImpactCount)}
							</InputLabel>
						</div>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<img
							className={styles['status-bar-stats-card-divider']}
							src={DividingLine}
							alt=""
						/>
					</span>
					<span className={styles['status-bar-stats-card-content-span']}>
						<InputLabel className={styles['status-bar-stats-card-header']}>
							{toSentenceCase('Low Impact')}
						</InputLabel>
						<div className={styles['row-container']}>
							<DialLow
								className={styles['status-bar-icon']}
								fill={chipPriorityLow}
							/>
							<InputLabel className={styles['status-bar-stats-card-number']}>
								{formatNumber(statusBarData.lowImpactCount)}
							</InputLabel>
						</div>
					</span>
				</>
			)}
		</Card>
	);
};

export default StatusBar;
